import { COMPONENT_VALUE_TYPES as types } from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

interface FormOptions {
  required: boolean;
  label: string;
}

export interface Props {
  header: {
    image: string;
    title: string;
    description: string;
  };
  form: {
    heading: {
      title: string;
      description: string;
    };
    email: FormOptions;
    firstName: FormOptions;
    lastName: FormOptions;
    mobile: FormOptions;
    zipCode: FormOptions;
  };
  footer: {
    disclaimerText: string;
    buttonText: string;
  };
  successMessage: {
    header: string;
    body: string;
    button: {
      buttonText: string;
      buttonLink: string;
    };
  };
}

const propsSchema: PropsSchemaList = [
  {
    key: 'header',
    label: 'Header',
    type: types.OBJECT,
    children: [
      {
        key: 'image',
        label: 'Image',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'description',
        label: 'Description',
        type: types.TEXTAREA,
      },
    ],
  },
  {
    key: 'form',
    label: 'Form',
    type: types.OBJECT,
    children: [
      {
        key: 'heading',
        label: 'Form Heading',
        type: types.OBJECT,
        children: [
          {
            key: 'title',
            label: 'Title',
            type: types.TEXT,
          },
          {
            key: 'description',
            label: 'Description',
            type: types.TEXTAREA,
          },
        ]
      },
      {
        key: 'email',
        label: 'Email',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Email Text',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'firstName',
        label: 'First Name',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'First Name Text',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'lastName',
        label: 'Last Name',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Last Name Text',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'mobile',
        label: 'Phone',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Phone Text',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'zipCode',
        label: 'Zipcode',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Zipcode Text',
            type: types.TEXT,
          },
        ]
      },
    ],
  },
  {
    key: 'footer',
    label: 'Footer',
    type: types.OBJECT,
    children: [
      {
        key: 'disclaimerText',
        label: 'Disclaimer Text',
        type: types.TINYMCE,
        options: {
          menubar: false,
          toolbar: 'fontsizeselect fontWeight italic underline link lineheight forecolor',
          height: 200,
        },
      },
      {
        key: 'buttonText',
        label: 'Submit Button Text',
        type: types.TEXT,
      },
    ],
  },
  {
    key: 'successMessage',
    label: 'Success Message',
    type: types.OBJECT,
    children: [
      {
        key: 'header',
        label: 'Header',
        type: types.TEXT,
      },
      {
        key: 'body',
        label: 'Body',
        type: types.TEXTAREA,
      },
      {
        key: 'button',
        label: 'Button',
        type: types.OBJECT,
        children: [
          {
            key: 'buttonText',
            label: 'Text',
            type: types.TEXT,
          },
          {
            key: 'buttonLink',
            label: 'Link',
            type: types.TEXT,
          },
        ]
      },
    ],
  },
];

const defaultProps: Props = {
  header: {
    image: 'https://genesis-cdn.ff.com/assets/images/sophie/new-dynamic-default.jpg',
    title: 'Experience the New Species: FF 91 2.0 Futurist Alliance',
    description: 'You can now apply for a demo drive to experience the ultimate driving.',
  },
  form: {
    heading: {
      title: 'Demo Drive Application',
      description: 'Experience the pure joy of driving firsthand, please complete the registration below and fill in the necessary information to submit your request.',
    },
    email: {
      required: true,
      label: 'Email',
    },
    firstName: {
      required: true,
      label: 'First Name',
    },
    lastName: {
      required: true,
      label: 'Last Name',
    },
    mobile: {
      required: true,
      label: 'Phone',
    },
    zipCode: {
      required: true,
      label: 'ZIP Code',
    },
  },
  footer: {
    buttonText: 'Submit application',
    disclaimerText: `<p>I have read and hereby agree to the terms and conditions set forth in the Faraday Future Release of Liability and Waiver Rights and confirm that I'm above the age of 18 and have a valid driver's license. In addition, I understand that I must bring a signed copy of this form waiver with me to participate in any demo ride/drive activities associated with Faraday Future. Furthermore, I understand that I am not permitted to use the vehicle outside of a designated Faraday Future facility space under any circumstances.</p>`
  },
  successMessage: {
    header: 'Submitted Successfully!',
    body: 'We are delighted to receive your test drive application and will contact you as soon as possible to ensure you embark on a luxurious driving experience.',
    button: {
      buttonText: 'Learn more about FF 91 2.0 Futurist Alliance',
      buttonLink: 'https://www.ff.com',
    },
  },
};

export default {
  title: 'Demo Drive Application',
  id: 'DemoDriveApplication',
  propsSchema,
  defaultProps,
}
